import { model, graphics, exhaust } from "GenAlphaModel";
import { schema } from "GenAlphaSchema";
import * as style from "../style";
import type {
  DrawTo,
  RunFn,
  HasXform,
  InstanceDrawResult,
  LayoutDisplayOptions,
  SchematicDisplayOptions,
} from "./drawto";
import type { OverlayKind, RenderStats } from "../stats";

//
// # DrawToBase
//
// Helper base class for implementing `DrawTo`.
// Provides default implementations for the things which
// should probably be identical across implementations.
//
export abstract class DrawToBase implements DrawTo {
  //
  // World transform information.
  // Defaults to scale=1, no viewport. Concrete implementations (e.g. DrawToHtmlCanvas)
  // override these with real values from the canvas world transforms.
  readonly scale: number = 1;
  readonly viewport: model.Bbox | null = null;

  // Rendering context defaults.
  // Concrete implementations derive these from the HtmlCanvas root,
  // which gets them set by LayoutContent before each render.
  readonly pdk: schema.pdk.PDK | null = null;
  readonly hiddenLayers: ReadonlySet<number> = new Set();

  // Layout display options — null when no layout is active.
  readonly layoutDisplay: LayoutDisplayOptions | null = null;

  // Schematic label visibility — null when no schematic is active.
  readonly schematicDisplay: SchematicDisplayOptions | null = null;

  // Per-frame render stats. Null on backends that don't track stats.
  // Concrete implementations override with a real `RenderStats` instance.
  readonly stats: RenderStats | null = null;

  // Default overlay implementation: just run `fn`, no stats tagging.
  // Concrete implementations override to track overlay kind.
  overlay(_kind: OverlayKind, fn: RunFn): unknown {
    return fn(this);
  }

  // Default overlay-cull bumps: no-op on backends that don't track stats.
  overlayCulled(): void {}
  overlaySizeCulled(): void {}

  // Default element-cull bumps: no-op on backends that don't track stats.
  elementCulled(): void {}
  elementSizeCulled(): void {}

  // Abstract/ required methods
  //
  abstract group(xform: model.Xform, fn: RunFn): unknown;
  abstract layer(fn: RunFn): unknown;
  abstract bbox(data: graphics.Bbox, s: style.ShapeStyle): unknown;
  abstract arc(data: graphics.Arc, s: style.ShapeStyle): unknown;
  abstract ellipse(data: graphics.Ellipse, s: style.ShapeStyle): unknown;
  abstract path(data: graphics.Path, s: style.ShapeStyle): unknown;
  abstract polygon(data: graphics.Polygon, s: style.ShapeStyle): unknown;
  abstract text(
    data: graphics.Text,
    s: style.TextStyle,
    parent: HasXform
  ): unknown;

  //
  // Default provided methods
  //

  // Drawing a rectangle routes to its inner `bbox`
  rect(data: graphics.Rectangle, s: style.ShapeStyle): unknown {
    return this.bbox(data.bbox, s);
  }

  // Draw a shape
  shape(data: graphics.Shape, s: style.ShapeStyle): unknown {
    // Dispatch over the shape kind
    const { kind } = data;
    switch (kind) {
      case "ellipse":
        return this.ellipse(data, s);
      case "rectangle":
        return this.rect(data, s);
      case "path":
        return this.path(data, s);
      case "polygon":
        return this.polygon(data, s);
      case "arc":
        return this.arc(data, s);
      default:
        exhaust(kind);
    }
  }

  element(data: graphics.Element, s: style.Style, parent: HasXform): unknown {
    if (data.kind === "text") return this.text(data, s.text, parent);
    return this.shape(data, s.shape);
  }

  shapes(data: Array<graphics.Shape>, s: style.ShapeStyle): unknown {
    return data.map((shape) => this.shape(shape, s));
  }

  elements(
    data: Array<graphics.Element>,
    s: style.Style,
    parent: HasXform
  ): unknown {
    return data.map((element) => this.element(element, s, parent));
  }

  // Instance drawing
  // Default implementation just delegates to `group`,
  // without any special instancing or handling. The `fn`'s return value is
  // *deliberately discarded* — `instance()` returns its own draw outcome so
  // nested `instance()` calls inside `fn` cannot influence this return.
  instance(
    key: object,
    bbox: model.Bbox,
    xform: model.Xform,
    fn: RunFn
  ): InstanceDrawResult {
    this.group(xform, fn);
    return "drawn";
  }
}
